import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Heart, Mail, ArrowRight } from 'lucide-react';
import Button from './Button';
import './DonateSuccessPage.css';

const DonateSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [donationDetails, setDonationDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const paymentIntentId = searchParams.get('payment_intent');
  const paymentIntentClientSecret = searchParams.get('payment_intent_client_secret');

  useEffect(() => {
    // Get donation details from URL parameters or localStorage
    const urlParams = new URLSearchParams(window.location.search);
    const amount = urlParams.get('amount') || localStorage.getItem('lastDonationAmount') || '10';
    const donorName = urlParams.get('donorName') || localStorage.getItem('lastDonorName') || 'Anonymous';
    const isRecurring = urlParams.get('isRecurring') === 'true';
    const frequency = urlParams.get('frequency') || 'monthly';

    setDonationDetails({
      amount: parseFloat(amount),
      type: isRecurring ? 'recurring' : 'one-time',
      frequency: frequency,
      donorName: donorName,
      date: new Date().toLocaleDateString()
    });
    
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <div className="donate-success-page">
        <div className="container">
          <div className="loading-container">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="loading-spinner"
            >
              <Heart size={32} />
            </motion.div>
            <p>Processing your donation...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="donate-success-page">
      <section className="success-hero">
        <div className="container">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="success-content"
          >
            <div className="success-icon">
              <CheckCircle size={64} />
            </div>
            <h1 style={{ color: 'white' }}>Thank You for Your Donation!</h1>
            <p>
              Your generous contribution will help us continue Carly's mission of 
              empowering young women through academic and athletic scholarships.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="success-details">
        <div className="container">
          <div className="success-grid">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="donation-summary"
            >
              <h2>Donation Summary</h2>
              <div className="summary-card">
                <div className="summary-row">
                  <span>Amount:</span>
                  <span>${donationDetails?.amount?.toFixed(2)}</span>
                </div>
                <div className="summary-row">
                  <span>Type:</span>
                  <span>{donationDetails?.type === 'recurring' ? 'Recurring' : 'One-Time'}</span>
                </div>
                {donationDetails?.type === 'recurring' && (
                  <div className="summary-row">
                    <span>Frequency:</span>
                    <span>{donationDetails?.frequency === 'monthly' ? 'Monthly' : 'Yearly'}</span>
                  </div>
                )}
                <div className="summary-row">
                  <span>Date:</span>
                  <span>{donationDetails?.date}</span>
                </div>
                <div className="summary-row">
                  <span>Donor:</span>
                  <span>{donationDetails?.donorName}</span>
                </div>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="next-steps"
            >
              <h2>What Happens Next?</h2>
              
              <div className="steps-list">
                <div className="step">
                  <div className="step-icon">
                    <Mail size={24} color="#00ccb8" />
                  </div>
                  <div className="step-content">
                    <h3>Confirmation Email</h3>
                    <p>You'll receive a confirmation email with your donation receipt within the next few minutes.</p>
                  </div>
                </div>

                <div className="step">
                  <div className="step-icon">
                    <Heart size={24} color="#00ccb8" />
                  </div>
                  <div className="step-content">
                    <h3>Impact Updates</h3>
                    <p>We'll keep you updated on how your donation is making a difference in students' lives.</p>
                  </div>
                </div>

                {donationDetails?.type === 'recurring' && (
                  <div className="step">
                    <div className="step-icon">
                      <CheckCircle size={24} color="#00ccb8" />
                    </div>
                    <div className="step-content">
                      <h3>Recurring Donation</h3>
                      <p>Your recurring donation will continue automatically. You can manage it anytime through your email.</p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="success-actions"
          >
            <div className="action-buttons">
              <Button href="/" variant="secondary">
                Return Home
              </Button>
              <Button href="/about" variant="primary" icon={ArrowRight}>
                Learn More About Our Mission
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default DonateSuccessPage; 